// API service layer for communicating with Django backend

// Use environment variable for API base URL, fallback to proxy for development
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api/v1'

class ApiService {
  constructor() {
    this.baseURL = API_BASE_URL
    
    // Log the environment in development mode
    if (import.meta.env.VITE_DEBUG === 'true') {
      console.log('API Service initialized:', {
        environment: import.meta.env.VITE_ENV_NAME || 'development',
        apiUrl: this.baseURL,
        debug: import.meta.env.VITE_DEBUG
      })
    }
  }

  async request(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`
    
    // Don't set Content-Type for FormData - let browser set it with boundary
    const isFormData = options.body instanceof FormData
    
    const config = {
      headers: {
        ...(!isFormData && { 'Content-Type': 'application/json' }),
        ...options.headers,
      },
      credentials: 'include', // Include cookies for session-based auth
      ...options,
    }

    // Add CSRF token for POST, PUT, PATCH, DELETE requests
    if (['POST', 'PUT', 'PATCH', 'DELETE'].includes(config.method?.toUpperCase())) {
      // Try to get CSRF token from cookies or meta tag
      const csrfToken = this.getCSRFToken()
      if (csrfToken) {
        config.headers['X-CSRFToken'] = csrfToken
      }
    }

    try {
      const response = await fetch(url, config)
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: response.statusText }))
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`)
      }
      
      return await response.json()
    } catch (error) {
      console.error(`API request failed: ${config.method || 'GET'} ${url}`, error)
      throw error
    }
  }

  getCSRFToken() {
    // Try to get CSRF token from cookie (Django default)
    const cookies = document.cookie.split(';')
    
    // Debug logging in development/production to see available cookies
    if (import.meta.env.VITE_DEBUG === 'true' || window.location.hostname.includes('pedipiggy.com')) {
      console.log('Available cookies:', document.cookie)
      console.log('Looking for CSRF token...')
    }
    
    for (let cookie of cookies) {
      const [name, value] = cookie.trim().split('=')
      if (name === 'csrftoken') {
        console.log('CSRF token found in cookie:', value.substring(0, 10) + '...')
        return decodeURIComponent(value)
      }
    }
    
    // Try to get CSRF token from meta tag
    const metaTag = document.querySelector('meta[name="csrf-token"]')
    if (metaTag) {
      const token = metaTag.getAttribute('content')
      console.log('CSRF token found in meta tag:', token.substring(0, 10) + '...')
      return token
    }
    
    // Try to get from window object if set by Django
    if (window.csrfToken) {
      console.log('CSRF token found in window object:', window.csrfToken.substring(0, 10) + '...')
      return window.csrfToken
    }
    
    console.warn('CSRF token not found in cookies, meta tag, or window object')
    console.warn('Document cookies:', document.cookie)
    return null
  }

  // HTTP method helpers
  async get(endpoint, params = {}) {
    const queryString = new URLSearchParams(params).toString()
    const url = queryString ? `${endpoint}?${queryString}` : endpoint
    
    return this.request(url, { method: 'GET' })
  }

  async post(endpoint, data) {
    return this.request(endpoint, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async put(endpoint, data) {
    return this.request(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  }

  async patch(endpoint, data) {
    return this.request(endpoint, {
      method: 'PATCH',
      body: JSON.stringify(data),
    })
  }

  async delete(endpoint) {
    return this.request(endpoint, { method: 'DELETE' })
  }

  // Specific API endpoints
  async healthCheck() {
    return this.get('/health/')
  }

  async testConnection(data = {}) {
    return this.post('/test/', data)
  }

  // Authentication API methods
  async register(userData) {
    return this.post('/auth/register/', userData)
  }

  async login(credentials) {
    return this.post('/auth/login/', credentials)
  }

  async logout() {
    return this.post('/auth/logout/')
  }

  async getProfile() {
    return this.get('/auth/profile/')
  }

  async getAuthStatus() {
    return this.get('/auth/status/')
  }

  async fetchCSRFToken() {
    const response = await this.get('/auth/csrf-token/')
    // Store the token in window object for later use
    if (response.csrf_token) {
      window.csrfToken = response.csrf_token
      console.log('CSRF token stored from server:', response.csrf_token.substring(0, 10) + '...')
    }
    return response
  }

  // Counter API methods (examples for the demo)
  async getCounterValue() {
    // This would call a real Django endpoint for counter
    // For now, simulate with test endpoint
    return this.get('/test/')
  }

  async updateCounter(value) {
    return this.post('/test/', { counter: value })
  }

  // Admin API methods
  async getAdminStats() {
    return this.get('/admin/stats/')
  }

  async getAdminUsers(params = {}) {
    return this.get('/admin/users/', params)
  }

  async getAdminUser(userId) {
    return this.get(`/admin/users/${userId}/`)
  }

  async updateAdminUser(userId, data) {
    return this.patch(`/admin/users/${userId}/`, data)
  }

  async getAdminSellers(params = {}) {
    return this.get('/admin/princesses/', params)
  }

  async updateAdminSeller(sellerId, data) {
    return this.patch(`/admin/princesses/${sellerId}/`, data)
  }

  async getAdminSellerIDVerification(sellerId) {
    return this.get(`/admin/princesses/${sellerId}/id-verification/`)
  }

  async updateAdminSellerIDVerification(sellerId, data) {
    return this.patch(`/admin/princesses/${sellerId}/id-verification/`, data)
  }

  // Product API methods
  async getProducts(params = {}) {
    return this.get('/products/', params)
  }

  async getProduct(productId) {
    return this.get(`/products/${productId}/`)
  }

  // Seller product management methods
  async getSellerProducts(params = {}) {
    return this.get('/seller/products/', params)
  }

  async createProduct(productData) {
    return this.post('/seller/products/', productData)
  }

  async getSellerProduct(productId) {
    return this.get(`/seller/products/${productId}/`)
  }

  async updateProduct(productId, data) {
    return this.put(`/seller/products/${productId}/`, data)
  }

  async deleteProduct(productId) {
    return this.delete(`/seller/products/${productId}/`)
  }

  // Princess (Seller) registration methods
  async registerSeller(sellerData) {
    return this.post('/princesses/register/', sellerData)
  }

  // Profile management methods
  async getUserProfile() {
    return this.get('/profile/')
  }

  async updateUserProfile(profileData) {
    return this.put('/profile/', profileData)
  }

  async uploadProfileImage(imageFile, imageType = 'profile') {
    const formData = new FormData()
    formData.append('image', imageFile)
    formData.append('image_type', imageType)
    
    return this.request('/profile/upload-image/', {
      method: 'POST',
      body: formData
      // Don't set headers here - the request method will handle CSRF token
    })
  }

  async setProfileImageAsPrimary(imageId) {
    return this.request(`/profile/images/${imageId}/`, {
      method: 'PUT',
      body: JSON.stringify({ is_primary: true })
    })
  }

  async deleteProfileImage(imageId) {
    return this.delete(`/profile/images/${imageId}/`)
  }

  // Subscription methods
  async getSubscription() {
    return this.get('/subscription/')
  }

  async getSubscriptionPlans() {
    return this.get('/subscription/plans/')
  }

  async createSubscription(plan) {
    return this.post('/subscription/', { plan })
  }

  async cancelSubscription() {
    return this.delete('/subscription/')
  }

  // Messaging methods
  async getMessages(params = {}) {
    const queryString = new URLSearchParams(params).toString()
    return this.get(`/messages/${queryString ? `?${queryString}` : ''}`)
  }

  async sendMessage(recipientId, content) {
    return this.post('/messages/send/', {
      recipient_id: recipientId,
      content
    })
  }

  async canMessageUser(userId) {
    return this.get(`/messages/can-message/${userId}/`)
  }

  async markMessageAsRead(messageId) {
    return this.patch(`/messages/${messageId}/`, { is_read: true })
  }

  async getConversation(userId, params = {}) {
    const queryString = new URLSearchParams(params).toString()
    return this.get(`/messages/conversation/${userId}/${queryString ? `?${queryString}` : ''}`)
  }

  // Special Request methods
  async getSpecialRequests(params = {}) {
    const queryString = new URLSearchParams(params).toString()
    return this.get(`/special-requests/${queryString ? `?${queryString}` : ''}`)
  }

  async createSpecialRequest(data) {
    return this.post('/special-requests/', data)
  }

  async getSpecialRequest(requestId) {
    return this.get(`/special-requests/${requestId}/`)
  }

  async acceptSpecialRequest(requestId) {
    return this.patch(`/special-requests/${requestId}/`, { action: 'accept' })
  }

  async rejectSpecialRequest(requestId) {
    return this.patch(`/special-requests/${requestId}/`, { action: 'reject' })
  }

  async counterOfferSpecialRequest(requestId, data) {
    return this.patch(`/special-requests/${requestId}/`, {
      action: 'counter_offer',
      ...data
    })
  }

  async acceptCounterOffer(requestId) {
    return this.patch(`/special-requests/${requestId}/`, { action: 'accept_counter' })
  }

  async cancelSpecialRequest(requestId) {
    return this.patch(`/special-requests/${requestId}/`, { action: 'cancel' })
  }

  // ID Verification methods
  async submitIDVerification(verificationData) {
    const formData = new FormData()
    
    // Add images
    if (verificationData.images?.selfie) {
      formData.append('selfie_with_id', verificationData.images.selfie)
    }
    if (verificationData.images?.front) {
      formData.append('front_of_id', verificationData.images.front)
    }
    if (verificationData.images?.back) {
      formData.append('back_of_id', verificationData.images.back)
    }
    
    // Add barcode data if available
    if (verificationData.barcodeData) {
      formData.append('frontend_barcode_data', JSON.stringify(verificationData.barcodeData))
    }
    
    // Add metadata
    if (verificationData.timestamp) {
      formData.append('frontend_timestamp', verificationData.timestamp)
    }
    
    return this.request('/id-verification/submit/', {
      method: 'POST',
      body: formData
    })
  }

  async getIDVerificationStatus() {
    return this.get('/id-verification/status/')
  }

  async getIDVerificationDetail() {
    return this.get('/id-verification/detail/')
  }

  async reprocessBarcode() {
    return this.post('/id-verification/reprocess-barcode/')
  }
}

// Create and export a singleton instance
const apiService = new ApiService()
export default apiService

// Named exports for specific API functions
export const api = apiService
export const { 
  healthCheck, 
  testConnection, 
  getCounterValue, 
  updateCounter,
  register,
  login,
  logout,
  getProfile,
  getAuthStatus,
  fetchCSRFToken
} = apiService